import { useNavigate, useParams } from 'react-router-dom'
import PageDropdown from '../../../components/page-dropdown/PageDropdown.jsx'
import Button from '../../../components/button/Button.jsx'
import { ErrorMessage, Form, Formik } from 'formik'
import * as Yup from 'yup'
import './branchForm.scss'
import NewInput from '../../../components/input/NewInput.jsx'
import { UI_CONSTANT, ROUTES_PATH } from '../../../utility/constants.js'
import BreadCrumbs from '../../../components/breadcrumb/Breadcrumb.jsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  generateDropdownOptions,
  handleEmail,
  handleInput,
  handleInputAddress,
  handleInputStringNoSpace,
  handleInputStringWithNum,
  handleName,
  handlePassword,
} from '../../../utility/genricFunction.js'
import { useDispatch, useSelector } from 'react-redux'
import { createBranch, editBranch, getSoloBranch } from '../../../store/builders/branch/branch.builder.js'
import {
  BRANCH_ALIAS_MAX_LENGTH,
  BRANCH_NAME_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
  RECEIPT_NOTE_MAX_LENGTH,
  VALIDATION_MESSAGES,
} from '../../../utility/constantValidationMessages.js'

const BranchForm = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { areas, states, cites } = useSelector((store) => store?.masterDataSet)
  const branchData = useSelector((store) => store?.branch?.soloBranch)
  const loading = useSelector((store) => store?.branch?.loading)
  const [initialValues, setInitialValues] = useState({
    branchName: '',
    branchMobileNumber: '',
    branchEmail: '',
    branchFullAddress: '',
    branchState: '',
    branchCity: '',
    branchArea: '',
    gstNo: '',
    cinNo: '',
    receiptNote: '',
    branchAlias: '',
    userFirstName: '',
    userLastName: '',
    userPhone: '',
    userEmail: '',
    userPassword: '',
  })
  const cityOption = useRef([])
  const areaOption = useRef([])

  useEffect(() => {
    if (id) {
      dispatch(getSoloBranch({ id }))
    }
    return () => dispatch({ type: 'branch/clearExtraReducers' })
  }, [id, dispatch])

  const checkBranchData = useCallback(() => {
    if (id) {
      setInitialValues({
        branchName: branchData.branchName,
        branchEmail: branchData.branchEmail,
        branchMobileNumber: branchData.branchPhone,
        branchFullAddress: branchData.addressLine,
        branchState: branchData.stateId,
        branchCity: branchData.cityId,
        branchArea: branchData.areaId,
        gstNo: branchData.gstNo,
        cinNo: branchData.cinNo,
        receiptNote: branchData.feeReceipt,
        branchAlias: branchData.branchAlias,
        userFirstName: branchData?.userName?.split(' ')[0],
        userLastName: branchData?.userName?.split(' ')?.pop(),
        userPhone: branchData.userPhone,
        userEmail: branchData.userEmail,
        userPassword: '',
      })
      const cities = cites.filter((item) => item.state_id === branchData.stateId)

      const cityOptions = cities.map((city) => ({
        label: city.city_name,
        value: city.city_id,
      }))
      const area = areas.filter((item) => item.city_id === branchData.cityId)

      const areaOptions = area.map((area) => ({
        label: area.area_name,
        value: area.area_id,
      }))
      areaOption.current = areaOptions?.length ? areaOptions : []
      cityOption.current = cityOptions?.length ? cityOptions : []
    }
  }, [id, branchData])
  useEffect(() => {
    checkBranchData()
  }, [id, branchData])

  const validationSchema = Yup.object().shape({
    branchName: Yup.string()
      .matches(/^\S*$/, VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_NAME_SPACE)
      .required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_NAME_REQUIRED)
      .max(BRANCH_NAME_MAX_LENGTH, VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_NAME_MAX),
    branchMobileNumber: Yup.string()
      .required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_PHONE_REQUIRED)
      .matches(/^[0-9]{10}$/, VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_PHONE_VALID),
    branchEmail: Yup.string()
      .matches(/\S+@\S+\.\S+/, VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_EMAIL_VALID)
      .required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_EMAIL_REQUIRED),
    branchFullAddress: Yup.string().required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_FULL_ADDRESS),
    branchState: Yup.string().required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_STATE),
    branchCity: Yup.string().required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_CITY),
    branchArea: Yup.string().required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_AREA),
    receiptNote: Yup.string()
      .required(VALIDATION_MESSAGES.BRANCH_FORM.RECEIPT_NOTE_REQUIRED)
      .max(RECEIPT_NOTE_MAX_LENGTH, VALIDATION_MESSAGES.BRANCH_FORM.RECEIPT_NOTE_MAX),
    branchAlias: Yup.string()
      .required(VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_ALIAS_REQUIRED)
      .max(BRANCH_ALIAS_MAX_LENGTH, VALIDATION_MESSAGES.BRANCH_FORM.BRANCH_ALIAS_MAX),
    userFirstName: Yup.string().required(VALIDATION_MESSAGES.BRANCH_FORM.USER_FIRST_NAME),
    userLastName: Yup.string().required(VALIDATION_MESSAGES.BRANCH_FORM.USER_LAST_NAME),
    userPhone: Yup.string()
      .required(VALIDATION_MESSAGES.BRANCH_FORM.USER_PHONE_REQUIRED)
      .matches(/^[0-9]{10}$/, VALIDATION_MESSAGES.BRANCH_FORM.USER_PHONE_VALID),
    userEmail: Yup.string()
      .matches(/\S+@\S+\.\S+/, VALIDATION_MESSAGES.BRANCH_FORM.USER_EMAIL_VALID)
      .required(VALIDATION_MESSAGES.BRANCH_FORM.USER_EMAIL_REQUIRED),
    userPassword: id
      ? Yup.string().optional()
      : Yup.string()
          .required(VALIDATION_MESSAGES.PASSWORD_REQUIRED)
          .min(PASSWORD_MIN_LENGTH, VALIDATION_MESSAGES.PASSWORD_VALID)
          .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            VALIDATION_MESSAGES.PASSWORD_VALID,
          ),
  })

  function handleFormSubmit(values) {
    const editObj = {
      stateId: values.branchState,
      areaId: values.branchArea,
      cityId: values.branchCity,
      addressLine: values.branchFullAddress,
      branchPhone: values.branchMobileNumber,
      branchEmail: values.branchEmail,
      branchGst: values.gstNo,
      branchCin: values.cinNo,
      feeReceipt: values.receiptNote,
      branchAlias: values.branchAlias,
      branchName: values.branchName,
      userPhone: values.userPhone,
      userName: values.userFirstName + ' ' + values.userLastName,
      userEmail: values.userEmail,
      userPassword: undefined,
    }
    if (id) {
      dispatch(editBranch({ id, editObj, navigate }))
    } else {
      dispatch(createBranch({ values, navigate }))
    }
  }

  const breadCrumbsArr = [
    {
      title: id ? UI_CONSTANT.BRANCH.EDIT_BRANCH : UI_CONSTANT.BRANCH.LABEL_BRANCH,
      link: null,
    },
    {
      title: UI_CONSTANT.DASHBOARD,
      link: ROUTES_PATH.DASHBOARD,
    },
    {
      title: UI_CONSTANT.BRANCH.LABEL_BRANCH,
      link: ROUTES_PATH.BRANCH,
    },
    {
      title: id ? UI_CONSTANT.BRANCH.EDIT_BRANCH : UI_CONSTANT.BRANCH.NEW_BRANCH,
      link: id ? ROUTES_PATH.BRANCH_EDIT.replace(':id', id) : ROUTES_PATH.BRANCH_ADD,
    },
  ]
  const handleStateDropdownChange = (e, setFieldValue) => {
    e.preventDefault()
    const stateValue = e.target.value
    setFieldValue('branchState', stateValue)

    const cities = cites.filter((item) => item.state_id === stateValue)

    const cityOptions = cities.map((city) => ({
      label: city.city_name,
      value: city.city_id,
    }))
    cityOption.current = cityOptions?.length ? cityOptions : []
  }

  const handleCityDropdownChange = (e, setFieldValue) => {
    e.preventDefault()
    const cityValue = e.target.value
    setFieldValue('branchCity', cityValue)

    const area = areas.filter((item) => item.city_id === cityValue)

    const areaOptions = area.map((area) => ({
      label: area.area_name,
      value: area.area_id,
    }))
    areaOption.current = areaOptions?.length ? areaOptions : []
  }

  return (
    <>
      <div className='branchForm-wrapper'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleFormSubmit(values)
          }}
          enableReinitialize
        >
          {({ values, handleChange, handleSubmit, setFieldValue }) => (
            <Form>
              <div className='branchForm-header'>
                <BreadCrumbs breadCrumbsArr={breadCrumbsArr} />

                <div className='branchForm-header-right'>
                  <Button customClassName={'save-btn'} onClick={handleSubmit} disabled={loading}>
                    {loading ? UI_CONSTANT.LOADING : UI_CONSTANT.SAVE}
                  </Button>
                </div>
              </div>
              <div className='branchForm'>
                <div className='branchForm-inner-header'>
                  <h1>{UI_CONSTANT.BRANCH.BRANCH_INFORMATION}</h1>
                  <p>{UI_CONSTANT.BRANCH.FORM_DESCRIPTION}</p>
                </div>

                <div className='separatorLine'></div>

                <div className='branchForm-input-fields'>
                  <NewInput
                    label='Branch name'
                    placeholder='Enter branch name'
                    name='branchName'
                    // width={'32%'}
                    width={'100%'}
                    required={true}
                    onInput={handleName}
                  />
                  <NewInput
                    label='Mobile number'
                    placeholder='Enter branch mobile number'
                    name='branchMobileNumber'
                    // width={'32%'}
                    width={'100%'}
                    type={'text'}
                    required={true}
                    onInput={handleInput}
                    maxLength={10}
                  />
                  <NewInput
                    label='Email'
                    placeholder='Enter branch Email'
                    name='branchEmail'
                    // width={'32%'}
                    width={'100%'}
                    type={'email'}
                    required={true}
                    maxLength={50}
                    onInput={handleEmail}
                  />
                  <NewInput
                    label='Branch Address'
                    placeholder='Enter branch full address'
                    name='branchFullAddress'
                    // width={'98.8%'}
                    width={'100%'}
                    required={true}
                    maxLength={100}
                    onInput={handleInputAddress}
                  />
                  <div className='branchForm-input'>
                    <label htmlFor=''>
                      Branch state <span className='required-symbol'>*</span>
                    </label>
                    <PageDropdown
                      options={generateDropdownOptions(states, 'state_name', 'state_id')}
                      selectedValue={values.branchState}
                      className={'branchForm-dropdown'}
                      onChange={(e) => handleStateDropdownChange(e, setFieldValue)}
                      name={'branchState'}
                      placeholder={'Select state'}
                    />
                    <ErrorMessage name='branchState' component='div' className='error-msg' />
                  </div>
                  <div className='branchForm-input'>
                    <label htmlFor=''>
                      Branch city<span className='required-symbol'>*</span>
                    </label>
                    <PageDropdown
                      // options={generateDropdownOptions(cites, 'city_name', 'city_id')}
                      options={cityOption.current}
                      selectedValue={values.branchCity}
                      className={'branchForm-dropdown'}
                      onChange={(e) => handleCityDropdownChange(e, setFieldValue)}
                      name={'branchCity'}
                      placeholder={'Select city'}
                      disabled={!values.branchState}
                    />
                    <ErrorMessage name='branchCity' component='div' className='error-msg' />
                  </div>
                  <div className='branchForm-input'>
                    <label htmlFor=''>
                      Branch area<span className='required-symbol'>*</span>
                    </label>
                    <PageDropdown
                      // options={generateDropdownOptions(areas, 'area_name', 'area_id')}
                      options={areaOption.current}
                      selectedValue={values.branchArea}
                      className={'branchForm-dropdown'}
                      onChange={handleChange}
                      name={'branchArea'}
                      placeholder={'Select area'}
                      disabled={!values.branchCity}
                    />
                    <ErrorMessage name='branchArea' component='div' className='error-msg' />
                  </div>
                  <div className='separatorLine-m50'></div>
                  <NewInput
                    label='GST No'
                    placeholder='Enter GST No'
                    name='gstNo'
                    // width={'32%'}
                    width={'100%'}
                    onInput={handleInputStringWithNum}
                    maxLength={15}
                  />
                  <NewInput
                    label='CIN No'
                    placeholder='Enter CIN No'
                    name='cinNo'
                    // width={'32%'}
                    width={'100%'}
                    onInput={handleInputStringWithNum}
                    maxLength={21}
                  />
                  <NewInput
                    label='Fee Receipt Note'
                    placeholder='Enter Fee Receipt Note'
                    name='receiptNote'
                    // width={'32%'}
                    width={'100%'}
                    required={true}
                    maxLength={100}
                    onInput={handleInputStringWithNum}
                  />
                  <NewInput
                    label='Branch Alias'
                    placeholder='Enter Branch Alias'
                    name='branchAlias'
                    // width={'32%'}
                    width={'100%'}
                    required={true}
                    onInput={handleName}
                  />
                </div>
              </div>

              <div className='branchForm'>
                <div className='branchForm-inner-header'>
                  <h1>{UI_CONSTANT.BRANCH.USER_DETAILS}</h1>
                  <p>{UI_CONSTANT.BRANCH.FORM_DESCRIPTION}</p>
                </div>

                <div className='separatorLine'></div>

                <div className='branchForm-input-fields'>
                  <NewInput
                    label='User First Name'
                    placeholder='User first name'
                    name='userFirstName'
                    // width={'32%'}
                    width={'100%'}
                    required={true}
                    onInput={handleInputStringNoSpace}
                  />
                  <NewInput
                    label='User Last Name'
                    placeholder='User last name'
                    name='userLastName'
                    // width={'32%'}
                    width={'100%'}
                    required={true}
                    onInput={handleInputStringNoSpace}
                  />
                  <div className='empty-space'></div>

                  <NewInput
                    label='Phone No'
                    placeholder='Enter phone no'
                    name='userPhone'
                    // width={'32%'}
                    width={'100%'}
                    type={'text'}
                    required={true}
                    onInput={handleInput}
                    maxLength={10}
                  />
                  <NewInput
                    label='Email'
                    placeholder='Enter email'
                    name='userEmail'
                    // width={'32%'}
                    width={'100%'}
                    type={'email'}
                    required={true}
                    onInput={handleEmail}
                    maxLength={50}
                  />

                  {!id && (
                    <NewInput
                      label='Password'
                      placeholder='Enter branch password'
                      name='userPassword'
                      // width={'32%'}
                      width={'100%'}
                      type={'password'}
                      required={true}
                      autoComplete='new-password'
                      onInput={handlePassword}
                    />
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </>
  )
}

export default BranchForm
